import { useNavigate, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import {
    PlantingOperation,
    ListApplications,
    TECHNIQUE_TYPES,
} from "@/types/operations";
import {
    Button,
    Autocomplete,
    AutocompleteItem,
    DateInput,
    NumberInput,
    Chip,
    Divider,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { object, string, InferType } from "yup";
import { OnDemandRxType, SimulationWeatherYear } from "@/types/enums";
import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import ScenarioPlannerIcon from "../nav/ScenarioPlannerIcon";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import SelectButtonInput from "../inputs/SelectButtonInput";
import { GenerateRxResponse } from "@/types/rx";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { CropCycleV2 } from "@/types/cropCycle";

const schema = object().shape({
    on_demand_type: string()
        .required()
        .oneOf(Object.values(OnDemandRxType))
        .default(OnDemandRxType.ShortTerm)
        .label("Sufficiency Outlook"),
    technique: string()
        .required()
        .oneOf(TECHNIQUE_TYPES.map((technique) => technique.value))
        .label("Technique"),
    weather_year: string()
        .oneOf(Object.values(SimulationWeatherYear))
        .when("on_demand_type", {
            is: (on_demand_type: OnDemandRxType) =>
                on_demand_type === OnDemandRxType.LastApplication,
            then: (weather_year) => weather_year.required(),
            otherwise: (weather_year) => weather_year.nullable(),
        })
        .label("Weather Scenario"),
});
type GenerateRxForm = InferType<typeof schema>;

function GenerateRx({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fieldId, selectedCropCycle, fieldQuery } =
        useFieldDashboard() ?? {};
    const navigate = useNavigate();
    const { cropCyclesV2Enabled } = useFeatureFlag();
    const techniqueOptions = useMemo(() => {
        return TECHNIQUE_TYPES.map((technique) => ({
            id: technique.value,
            description: technique.name,
        }));
    }, []);
    const onDemandTypeOptions = useMemo(() => {
        return [
            { id: OnDemandRxType.ShortTerm, description: "One Week" },
            { id: OnDemandRxType.TwoWeek, description: "2 Weeks" },
            { id: OnDemandRxType.FourWeek, description: "4 Weeks" },
            {
                id: OnDemandRxType.LastApplication,
                description: "Remainder of Season",
            },
        ];
    }, []);
    const weatherScenarioOptions = useMemo(() => {
        return Object.values(SimulationWeatherYear).map((year) => ({
            id: year,
            description: year,
        }));
    }, []);
    const form = useForm<GenerateRxForm>({
        resolver: yupResolver(schema),
    });
    const onDemandType = form.watch("on_demand_type");
    const plantingOperationsQuery = useGetRequest<PlantingOperation[]>({
        url: `fields/${fieldId}/planting_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });
    const plantingOperation = useMemo(() => {
        if (
            cropCyclesV2Enabled &&
            (selectedCropCycle as CropCycleV2)?.planting_operation
        ) {
            return plantingOperationsQuery?.data?.find(
                (op) =>
                    op.id ===
                    (selectedCropCycle as CropCycleV2)?.planting_operation,
            );
        }
        return plantingOperationsQuery?.data?.[0];
    }, [plantingOperationsQuery?.data, selectedCropCycle, cropCyclesV2Enabled]);
    const appliationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!selectedCropCycle?.start_date,
        params: {
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        },
    });
    const totalNApplied = useMemo(() => {
        const nApplied = appliationOperationsQuery?.data?.operations.reduce(
            (acc, application) => {
                return acc + application.average_N;
            },
            0,
        );
        return nApplied ? roundTo(nApplied, 1) : 0;
    }, [appliationOperationsQuery?.data]);

    const generateRxRequest = useMutateRequest<GenerateRxResponse>({
        url: `prescriptions/on_demand/`,
        method: "post",
        errorMessage: t("error.failed_to_generate_rx", { ns: "messages" }),
        successMessage: t("success.generated_rx", { ns: "messages" }),
        onSuccess: (data) => {
            navigate({
                to: `/dashboard/fields/${fieldId}/rx/${data.prescription_id}`,
            });
            onClose();
        },
    });
    const handleSubmit = form.handleSubmit((data: GenerateRxForm) => {
        generateRxRequest.mutate({
            data: {
                field: Number(fieldId),
                on_demand_type: data.on_demand_type,
                imagery: fieldQuery?.data?.recommendation.imagery_id,
                technique: data.technique,
                weather_year: data.weather_year,
            },
        });
    });
    const { formatUnit } = useI18n();

    return (
        <div className="flex flex-col p-2 gap-3 w-full items-center">
            <div className="flex flex-row items-center w-full justify-center">
                <img
                    src={allNHorizontalImage}
                    alt="All N Horizontal"
                    className="h-10 object-cover"
                />
            </div>
            {!plantingOperation ? (
                <div className="flex flex-row w-full justify-between items-center">
                    <p className="text-sm text-warning">
                        {t("no_planting_operation_found", { ns: "rx" })}
                    </p>
                    <Button
                        color="secondary"
                        size="sm"
                        as={Link}
                        to={`/dashboard/fields/${fieldId}/operations/add`}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {t("add_planting", { ns: "rx" })}
                    </Button>
                </div>
            ) : (
                <div className="flex flex-row items-end w-full justify-between gap-2">
                    <Autocomplete
                        label={t("crop", { ns: "rx" })}
                        labelPlacement="outside"
                        defaultSelectedKey={
                            plantingOperation?.crop_configuration?.crop_key
                        }
                        isReadOnly
                        size="sm"
                    >
                        <AutocompleteItem
                            key={
                                plantingOperation?.crop_configuration?.crop_key
                            }
                        >
                            {plantingOperation?.crop_configuration?.crop_name}
                        </AutocompleteItem>
                    </Autocomplete>
                    <DateInput
                        label={t("planting_date", { ns: "rx" })}
                        labelPlacement="outside"
                        isReadOnly
                        defaultValue={parseDate(plantingOperation.start_date)}
                        size="sm"
                    />
                    <Button
                        color="primary"
                        size="sm"
                        as={Link}
                        to={`/dashboard/fields/${fieldId}/operations/planting/${plantingOperation.id}`}
                    >
                        <FontAwesomeIcon icon={faPencil} className="mr-2" />
                        {t("edit", { ns: "common" })}
                    </Button>
                </div>
            )}
            <div className="flex flex-row w-full">
                {t("applications", { ns: "rx" })}:
            </div>
            {appliationOperationsQuery?.data?.operations.map((operation) => (
                <div
                    key={operation.id}
                    className="flex flex-row items-end w-full justify-between gap-2 "
                >
                    <DateInput
                        label={t("date", { ns: "rx" })}
                        labelPlacement="outside"
                        isReadOnly
                        defaultValue={parseDate(operation.start_date)}
                        size="sm"
                    />
                    <NumberInput
                        label={`${t("n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`}
                        labelPlacement="outside"
                        isReadOnly
                        defaultValue={operation.average_N}
                        size="sm"
                    />
                    <Button
                        color="primary"
                        size="sm"
                        as={Link}
                        to={`/dashboard/fields/${fieldId}/operations/application/${operation.id}`}
                    >
                        <FontAwesomeIcon icon={faPencil} className="mr-2" />
                        {t("edit", { ns: "common" })}
                    </Button>
                </div>
            ))}
            <div className="flex flex-row w-full justify-between items-center">
                {appliationOperationsQuery?.data?.operations.length === 0 ? (
                    <p className="text-sm text-warning">
                        {t("no_applications_found", { ns: "rx" })}
                    </p>
                ) : (
                    <div className="flex flex-row items-center gap-2">
                        <p>{t("total_n", { ns: "rx" })}:</p>
                        <Chip size="sm" color="primary" variant="flat">
                            {totalNApplied ?? "-"} {formatUnit("lb_ac", "kg_ha").replace("/", " N/")}
                        </Chip>
                    </div>
                )}
                <Button
                    color="secondary"
                    size="sm"
                    as={Link}
                    to={`/dashboard/fields/${fieldId}/operations/add`}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("add_application", { ns: "rx" })}
                </Button>
            </div>
            <p className="flex flex-row w-full  text-sm text-gray-500 justify-end">
                {t("check_operations_up_to_date", { ns: "rx" })}
            </p>
            <Divider className="w-full" />
            <FormProvider {...form}>
                <form
                    className="flex flex-col w-full gap-2 text-sm"
                    onSubmit={handleSubmit}
                >
                    <div className="flex flex-row w-full justify-between">
                        <AutoCompleteInput
                            label={t("application_technique", { ns: "rx" })}
                            labelPlacement="outside"
                            name="technique"
                            placeholder="How will you apply this Rx?"
                            options={techniqueOptions}
                            fullWidth={true}
                            isRequired={true}
                        />
                    </div>
                    <div className="flex flex-row w-full justify-between">
                        <SelectButtonInput
                            label={t("sufficiency_outlook", { ns: "rx" })}
                            name="on_demand_type"
                            orientation="horizontal"
                            isRequired={true}
                            options={onDemandTypeOptions}
                            defaultValue={OnDemandRxType.ShortTerm}
                            description={t(
                                "how_long_do_you_want_this_application_to_last",
                                { ns: "rx" },
                            )}
                        />
                    </div>
                    {[
                        OnDemandRxType.FourWeek,
                        OnDemandRxType.LastApplication,
                    ].includes(onDemandType) && (
                        <div className="flex flex-row w-full justify-between">
                            <AutoCompleteInput
                                label={t("weather_scenario", { ns: "rx" })}
                                labelPlacement="outside"
                                name="weather_year"
                                placeholder={t("select_a_weather_scenario", {
                                    ns: "rx",
                                })}
                                options={weatherScenarioOptions}
                                fullWidth={true}
                                isRequired={true}
                            />
                        </div>
                    )}
                    <Button
                        fullWidth
                        color="secondary"
                        type="submit"
                        isLoading={generateRxRequest.isPending}
                    >
                        <ScenarioPlannerIcon color="white" />
                        {t("generate_rx", { ns: "rx" })}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
}

export default GenerateRx;
